/*
 * SPDX-FileCopyrightText: 2026 The HedgeDoc developers (see AUTHORS file)
 *
 * SPDX-License-Identifier: AGPL-3.0-only
 */
import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import { initialState } from './initial-state'
import { NoteType, type NoteTreeEntryInterface } from '@hedgedoc/commons'
import type { NoteTreeEntryOwnerUpdate, NoteTreeEntryRename, NoteTreeEntryUpdate } from './types'

const noteTreeSlice = createSlice({
  name: 'noteTree',
  initialState,
  reducers: {
    setNoteTreeEntries: (state, action: PayloadAction<NoteTreeEntryInterface[]>) => {
      state.loaded = true
      state.entries = {}
      for (const entry of action.payload) {
        state.entries[entry.primaryAlias] = entry
      }
    },
    updateNoteTreeEntry: (state, action: PayloadAction<NoteTreeEntryUpdate>) => {
      const { primaryAlias, title, path, badge } = action.payload
      const existingEntry = state.entries[primaryAlias]
      if (existingEntry === undefined) {
        state.entries[primaryAlias] = {
          primaryAlias,
          title,
          path,
          badge,
          type: NoteType.DOCUMENT,
          // The owner is unknown until the tree is refreshed from the backend
          owner: null,
          lastChangedAt: new Date().toISOString()
        }
      } else {
        existingEntry.title = title
        existingEntry.path = path
        existingEntry.badge = badge
      }
    },
    setNoteTreeEntryOwner: (state, action: PayloadAction<NoteTreeEntryOwnerUpdate>) => {
      const existingEntry = state.entries[action.payload.primaryAlias]
      if (existingEntry !== undefined) {
        existingEntry.owner = action.payload.owner
      }
    },
    renameNoteTreeEntry: (state, action: PayloadAction<NoteTreeEntryRename>) => {
      const { from, to } = action.payload
      const existingEntry = state.entries[from]
      if (existingEntry === undefined || from === to) {
        return
      }
      delete state.entries[from]
      state.entries[to] = { ...existingEntry, primaryAlias: to }
    },
    removeNoteTreeEntry: (state, action: PayloadAction<string>) => {
      delete state.entries[action.payload]
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload
    },
    resetNoteTree: (state) => ({ ...initialState, sidebarOpen: state.sidebarOpen })
  }
})

export const noteTreeActionsCreator = noteTreeSlice.actions
export const noteTreeReducer = noteTreeSlice.reducer
